import React from 'react';
import { motion } from 'framer-motion';
import { AcademicCapIcon, SparklesIcon, GlobeAltIcon, ClockIcon } from '@heroicons/react/24/outline';

const FFSlide02FutureOfSchooling: React.FC = () => {
  const pillars = [
    {
      icon: AcademicCapIcon,
      title: 'Teachers Augmented, Not Replaced',
      description:
        'AI will automate routine work (lesson planning time cut by up to 31%), freeing educators for mentoring, relationships, and deeper coaching amid a projected global shortage of 44 million teachers.',
      source: "UNESCO's Global Report on Teachers",
      color: '#10b981',
    },
    {
      icon: SparklesIcon,
      title: 'Built for This Future',
      description:
        "Grounded in learning psychology, Lumen8 adapts in real time to each child's cognitive development stage, preferred learning style and individual progress — a true teacher force-multiplier, not a competitor.",
      color: '#0ea5e9',
    },
    {
      icon: GlobeAltIcon,
      title: 'Modular & Global From Day One',
      description:
        'AI-native architecture starts anchored in Australia yet expands seamlessly to international curricula and languages, unlocking global reach.',
      color: '#8b5cf6',
    },
    {
      icon: ClockIcon,
      title: 'Perfect Timing',
      description:
        "In a macro shift demanding personalised, equitable, anywhere-anytime learning, Lumen8 doesn't just ride the wave — we are the precise tool the world's leading reports say every education system will need.",
      color: '#f59e0b',
    },
  ];

  return (
    <div className="flex flex-col items-center justify-center h-full px-16">
      <motion.h2
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-5xl font-bold text-white mb-3 text-center"
      >
        The Future of Schooling
      </motion.h2>
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
        className="text-lg text-[#94a3b8] mb-10 text-center max-w-4xl"
      >
        AI-Augmented, Personalised & Teacher-Empowered
      </motion.p>

      {/* Macro context banner */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="w-full max-w-5xl mb-8 p-5 rounded-xl text-center"
        style={{
          background: 'rgba(30, 41, 59, 0.6)',
          border: '1px solid rgba(255, 255, 255, 0.15)',
        }}
      >
        <p className="text-[#94a3b8] text-base leading-relaxed">
          OECD's <span className="text-white font-medium">Digital Education Outlook 2026</span> and
          UNESCO's <span className="text-white font-medium">Global Report on Teachers</span> confirm
          that by 2030-2040, rigid, one-size-fits-all classrooms will evolve into{' '}
          <span className="text-[#0ea5e9] font-semibold">
            hybrid, flexible, student-centered systems
          </span>{' '}
          focused on equity, future-ready skills, and lifelong learning.
        </p>
      </motion.div>

      {/* Four pillars */}
      <div className="grid grid-cols-4 gap-5 w-full max-w-6xl mb-8">
        {pillars.map((pillar, index) => (
          <motion.div
            key={pillar.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 + index * 0.1 }}
            className="p-5 rounded-xl"
            style={{
              background: `${pillar.color}10`,
              border: `1px solid ${pillar.color}40`,
            }}
          >
            <pillar.icon className="w-8 h-8 mb-3" style={{ color: pillar.color }} />
            <h3 className="text-base font-semibold text-white mb-2">{pillar.title}</h3>
            <p className="text-sm text-[#94a3b8] leading-relaxed">{pillar.description}</p>
            {pillar.source && (
              <p className="text-xs text-[#64748b] mt-2 italic">{pillar.source}</p>
            )}
          </motion.div>
        ))}
      </div>

      {/* Closing banner */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.9 }}
        className="px-10 py-5 rounded-2xl"
        style={{
          background:
            'linear-gradient(135deg, rgba(14, 165, 233, 0.2) 0%, rgba(139, 92, 246, 0.2) 100%)',
          border: '2px solid rgba(14, 165, 233, 0.4)',
        }}
      >
        <p className="text-xl text-white text-center font-semibold">
          This is Lumen8's moment.
        </p>
      </motion.div>
    </div>
  );
};

export default FFSlide02FutureOfSchooling;
